import React from 'react';
import { Mic, AlertTriangle, CheckCircle2 } from 'lucide-react';

export const MicLevelBar = ({ level, testId = 'mic-level-bar' }) => (
  <div className="flex items-end gap-0.5 h-4" data-testid={testId} aria-label={`Microphone level ${Math.round(level * 100)}%`}>
    {Array.from({ length: 12 }, (_, i) => (
      <span key={i} className={`w-1.5 rounded-sm transition-colors duration-75 ${level * 12 > i ? (i > 9 ? 'bg-amber-500' : 'bg-emerald-500') : 'bg-slate-200'}`}
        style={{ height: `${30 + i * 6}%` }} />
    ))}
  </div>
);

const MicPicker = ({ mic, prefix }) => (mic.devices.length > 1 ? (
  <select value={mic.micId} onChange={(e) => mic.setMicId(e.target.value)} disabled={mic.active}
    className="text-xs border border-slate-300 rounded-md px-1.5 py-1 bg-white max-w-[220px] truncate" data-testid={`${prefix}-mic-picker`}>
    <option value="">System default microphone</option>
    {mic.devices.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>)}
  </select>
) : null);

const Message = ({ mic, prefix }) => {
  if (mic.error) return <p className="text-xs text-rose-600 flex gap-1" data-testid={`${prefix}-mic-error`}><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />{mic.error}</p>;
  if (mic.active && mic.silent && !mic.heard) return <p className="text-xs text-amber-700 flex gap-1" data-testid={`${prefix}-mic-silent`}><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />We can't hear you. Speak up, or check that the right microphone is selected and not muted.</p>;
  if (mic.recording && mic.silent) return <p className="text-xs text-slate-500" data-testid={`${prefix}-mic-paused`}>Still recording. No sound for a few seconds.</p>;
  if (mic.testing) return <p className="text-xs text-slate-600" data-testid={`${prefix}-mic-testing`}>Say something. The bar should move when you talk.</p>;
  if (mic.testResult === 'ok') return <p className="text-xs text-emerald-700 flex gap-1" data-testid={`${prefix}-mic-test-ok`}><CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-px" />Microphone works. You're ready to dictate.</p>;
  if (mic.testResult === 'silent') return <p className="text-xs text-rose-600 flex gap-1" data-testid={`${prefix}-mic-test-silent`}><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />No sound picked up. Pick a different microphone, check it isn't muted, and test again.</p>;
  return null;
};

// Shows which mic is used, a picker, a Test mic button, the live level and any warnings.
export const MicCheck = ({ mic, prefix = 'dictation', className = '' }) => (
  <div className={`space-y-1.5 ${className}`} data-testid={`${prefix}-mic-check`}>
    <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600">
      <Mic className="h-3.5 w-3.5 text-slate-500" />
      <span className="truncate max-w-[200px]" data-testid={`${prefix}-mic-label`}>{mic.label || 'Microphone'}</span>
      <MicPicker mic={mic} prefix={prefix} />
      {mic.active ? <MicLevelBar level={mic.level} testId={`${prefix}-mic-level`} /> : (
        <button type="button" onClick={mic.test} className="text-sky-700 underline" data-testid={`${prefix}-mic-test-btn`}>Test mic</button>
      )}
    </div>
    <Message mic={mic} prefix={prefix} />
  </div>
);

export default MicCheck;
